import React from 'react';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui';
import type { Academy } from '@court-sync/shared';

export type PublicBookingSuccessAction = {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'outline';
};

export interface PublicBookingSuccessStepProps {
  hasPaymentProcessing: boolean;
  academy: Academy | null;
  actions: PublicBookingSuccessAction[];
}

export function PublicBookingSuccessStep({
  hasPaymentProcessing,
  academy,
  actions,
}: PublicBookingSuccessStepProps) {
  const title = hasPaymentProcessing
    ? 'Booking Confirmed!'
    : 'Booking request received, booking will be confirmed once payment is processed';
  const description = hasPaymentProcessing
    ? 'Your lesson has been successfully booked. You will receive a confirmation email shortly.'
    : 'You will receive a confirmation email shortly.';

  return (
    <div className="text-center py-8">
      <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
      <h3 className="text-2xl font-bold text-foreground mb-2">{title}</h3>
      <p className="text-muted-foreground mb-6">{description}</p>
      {!hasPaymentProcessing && academy?.paymentCollectionMessage && (
        <div className="mx-auto mb-6 max-w-2xl rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-950/35">
          <p className="mb-2 text-sm font-medium text-yellow-900 dark:text-yellow-100">Payment Information:</p>
          <p className="text-sm text-yellow-800 dark:text-yellow-200/90">{academy.paymentCollectionMessage}</p>
        </div>
      )}
      <div className="flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
        {actions.map((action) => (
          <Button
            key={action.label}
            variant={action.variant ?? 'default'}
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        ))}
      </div>
    </div>
  );
}
